const usd = new Intl.NumberFormat("en-US", {
  style: "currency",
  currency: "USD"
})

export function money(
  value: string | number | null | undefined,
  currency = "USD"
): string {
  if (value === null || value === undefined || value === "") return "—"
  const number = typeof value === "number" ? value : Number(value)
  if (Number.isNaN(number)) return "—"
  if (currency === "USD") return usd.format(number)
  return new Intl.NumberFormat("en-US", { style: "currency", currency }).format(
    number
  )
}

export function percent(
  value: string | number | null | undefined,
  digits = 1
): string {
  if (value === null || value === undefined || value === "") return "—"
  const number = Number(value)
  return Number.isNaN(number)
    ? "—"
    : `${number.toFixed(digits).replace(/\.0+$/, "")}%`
}

export function confidencePercent(
  value: string | null | undefined
): number | null {
  if (value === null || value === undefined || value === "") return null
  const number = Number(value)
  return Number.isNaN(number) ? null : Math.round(number * 100)
}

export function formatDate(value: string | null | undefined): string {
  if (!value) return "—"
  const date = new Date(value.length === 10 ? `${value}T00:00:00` : value)
  return Number.isNaN(date.getTime())
    ? "—"
    : date.toLocaleDateString(undefined, {
        year: "numeric",
        month: "short",
        day: "numeric"
      })
}

export function formatDateTime(value: string | null | undefined): string {
  if (!value) return "—"
  const date = new Date(value)
  return Number.isNaN(date.getTime())
    ? "—"
    : date.toLocaleString(undefined, {
        month: "short",
        day: "numeric",
        hour: "numeric",
        minute: "2-digit"
      })
}

export function newIdempotencyKey(): string {
  return typeof crypto !== "undefined" && "randomUUID" in crypto
    ? crypto.randomUUID()
    : `${Date.now()}-${Math.random().toString(36).slice(2)}`
}

type Translate = (key: string, options?: Record<string, unknown>) => string

const MONEY_FIELDS = new Set(["planned_receivable", "planned_value", "amount"])

// "Duration 5 → 3 days" rather than the raw field name "duration_days 5 → 3".
export function fieldChanges(
  t: Translate,
  diff: Record<string, unknown>
): string[] {
  return Object.entries(diff)
    .filter(([, value]) => Array.isArray(value))
    .map(([field, value]) => {
      const [before, after] = value as [unknown, unknown]
      const key = `aiCenter.sowReceivables.fields.${field}`
      const label = t(key)
      return t("aiCenter.sowReceivables.fields.change", {
        field: label === key ? field.replace(/_/g, " ") : label,
        before: MONEY_FIELDS.has(field)
          ? money(before as string | null)
          : (before ?? "—"),
        after: MONEY_FIELDS.has(field)
          ? money(after as string | null)
          : (after ?? "—"),
        unit:
          field === "duration_days"
            ? ` ${t("aiCenter.sowReceivables.fields.days")}`
            : ""
      })
    })
}

// "14 milestones · 1 payment" — parts with nothing in them are left out.
export function countParts(
  t: Translate,
  counts: { milestones: number; payments: number }
): string {
  return [
    counts.milestones
      ? t("aiCenter.sowReceivables.common.milestoneCount", {
          count: counts.milestones
        })
      : "",
    counts.payments
      ? t("aiCenter.sowReceivables.common.paymentCount", {
          count: counts.payments
        })
      : ""
  ]
    .filter(Boolean)
    .join(" · ")
}
